/**
 * EditForm component for modifying an existing dataset.
 * This component uses PopupWrapper and follows the modern application structure,
 * providing a form interface for viewing and editing dataset properties.
 */

import React, { useState, useEffect, useRef } from "react";
import PopupWrapper from "../../../../common/pyramid/PopupWrapper";
import { Form, SimpleItem, GroupItem, Label, ButtonItem } from "devextreme-react/form";
import Button from "devextreme-react/button";
import { useAuth } from "providers/AuthProvider";
import useItemOwner from "hooks/useItemOwner";
import { osirisApi } from "services/api/osiris";
import { OSIRIS_DATASET_TYPES } from "data/ENUM/add-options";

/**
 * Default popup configuration for the EditForm component
 * Defines dimensions, title and icon for the popup
 */
export const popupConfigs = {
  width: "auto",
  height: "auto",
  title: "Edit Dataset",
  icon: "edit",
};

/**
 * EditForm component allows users to view dataset details.
 *
 * @param {Object} props - Component properties
 * @param {boolean} props.isVisible - Controls the visibility of the popup
 * @param {Function} props.onCancel - Callback function when the operation is cancelled
 * @param {Function} props.onConfirm - Callback function when changes are confirmed
 * @param {Object} props.dataset - The dataset object to be edited
 */
const EditForm = ({ isVisible, onCancel, onConfirm, dataset }) => {
  /**
   * Authentication context providing access to company and user lists
   */
  const { companyList, userList } = useAuth();
  const api = osirisApi();

  /**
   * State management for the component
   */
  const [isLoading, setIsLoading] = useState(false); // Loading state for form submission
  const [formData, setFormData] = useState(dataset || {}); // Form data state
  const [isDownloading, setIsDownloading] = useState(false);

  /**
   * Reference to the form component for potential direct manipulation
   */
  const formRef = useRef(null);

  //Check if the item belongs to my group
  const ownedItem = useItemOwner(dataset, "group");

  /**
   * Update form data when dataset prop changes
   */
  useEffect(() => {
    if (dataset) {
      setFormData({ ...dataset });
    }
  }, [dataset]);

  const handleDownloadInputs = async (datasetType) => {
    if (!datasetType || !dataset?.id) return;
    setIsDownloading(true);
    try {
      await api.downloadInputFiles({
        id: dataset.id,
        datasetTypes: [datasetType],
      });
    } catch (error) {
      console.error("Error downloading input files:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  /**
   * Handles the form submission when the user confirms changes
   */
  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const updatedData = {
        ...dataset,
        description: formData.description,
      };
      await onConfirm(updatedData);
    } catch (error) {
      console.error("Error updating dataset:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PopupWrapper
      {...popupConfigs}
      isVisible={isVisible}
      onCancel={onCancel}
      onConfirm={handleSubmit}
      isConfirmDisabled={!ownedItem}
    >
      <Form
        ref={formRef}
        formData={formData}
        onFieldDataChanged={(e) =>
          setFormData({ ...formData, [e.dataField]: e.value })
        }
      >
        {/* Dataset Information Section */}
        <GroupItem caption="Informations" colCount={2}>
          <SimpleItem
            colSpan={2}
            dataField="description"
            editorType="dxTextBox"
            isRequired={true}
            editorOptions={{
              readOnly: !ownedItem,
            }}
          >
            <Label text="Description" />
          </SimpleItem>

          <SimpleItem
            dataField="evaluationDate"
            editorType="dxDateBox"
            editorOptions={{
              displayFormat: "yyyy-MM-dd",
              readOnly: true,
            }}
            isRequired={true}
          >
            <Label text="Evaluation Date" />
          </SimpleItem>
        </GroupItem>

        {/* Audit Information Section */}
        <GroupItem caption="Audit" colCount={2}>
          <SimpleItem
            colSpan={2}
            dataField="createdAt"
            editorType="dxDateBox"
            editorOptions={{
              displayFormat: "yyyy-MM-dd",
              readOnly: true,
            }}
            isRequired={true}
          >
            <Label text="Creation Date" />
          </SimpleItem>

          <SimpleItem
            dataField="groupId"
            editorType="dxSelectBox"
            editorOptions={{
              readOnly: true,
              dataSource: companyList,
              displayExpr: "displayName",
              valueExpr: "id",
            }}
          >
            <Label text="Created by company" />
          </SimpleItem>

          <SimpleItem
            dataField="userId"
            editorType="dxSelectBox"
            editorOptions={{
              readOnly: true,
              dataSource: userList,
              displayExpr: "displayName",
              valueExpr: "id",
            }}
          >
            <Label text="Created by user" />
          </SimpleItem>
        </GroupItem>

        <GroupItem caption="Download inputs" colCount={4}>
          <SimpleItem
            colSpan={4}
            render={() => (
              <div>
                <div style={{ marginBottom: 8 }}>Select an input type:</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {OSIRIS_DATASET_TYPES.map((type) => (
                    <Button
                      key={type.id}
                      text={type.label}
                      icon="download"
                      type="default"
                      stylingMode="contained"
                      disabled={isDownloading}
                      onClick={() => handleDownloadInputs(type.id)}
                    />
                  ))}
                </div>
              </div>
            )}
          />
        </GroupItem>
      </Form>
    </PopupWrapper>
  );
};

export default EditForm;
